import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from "react";
import { SingleResourceProvider } from "./context";
import { jsx as _jsx } from "react/jsx-runtime";
class ResourceError extends Error {
  constructor(message) {
    super(message);
    this.name = "ResourceError";
  }
}
/**
 *  Resource class
 *  The react suspense API might change in the future.
 *  Refer to https://reactjs.org/docs/concurrent-mode-suspense.html from API changes
 */
export class Resource {
  constructor(getter) {
    _defineProperty(this, "resource", undefined);
    _defineProperty(this, "error", undefined);
    _defineProperty(this, "status", "unprepared");
    _defineProperty(this, "suspender", undefined);
    _defineProperty(this, "suspendPromise", promise => {
      return promise.then(r => {
        this.status = "success";
        this.resource = r;
      }, e => {
        this.status = "error";
        this.error = e;
      });
    });
    _defineProperty(this, "prepare", optimistic => {
      if (this.status === "unprepared" || typeof this.suspender === "undefined") {
        if (typeof optimistic === "undefined") {
          this.suspender = this.suspendPromise(this.getter());
          this.status = "pending";
        } else {
          this.suspender = Promise.resolve();
          this.status = "success";
          this.resource = optimistic;
        }
      }
      return this;
    });
    _defineProperty(this, "prepareAsUndefined", () => {
      if (this.status === "unprepared" || typeof this.suspender === "undefined") {
        this.suspender = Promise.resolve();
        this.status = "success";
        this.resource = undefined;
      }
      return this;
    });
    _defineProperty(this, "fetchResource", () => {
      return this.getter();
    });
    _defineProperty(this, "setLocalResource", newResource => {
      this.resource = newResource;
      this.status = "success";
    });
    _defineProperty(this, "getSuspenderRe", () => {
      if (this.status === "unprepared" || typeof this.suspender === "undefined") {
        throw new ResourceError("Cannot get suspender ref, since resource was not initialized. Make sure to call prepare on the resource before reading it!");
      }
      return this.suspender;
    });
    _defineProperty(this, "reload", () => {
      this.suspender = this.suspendPromise(this.getter());
      this.status = "pending";
    });
    /**
     * Experimental suspender
     * The react suspense API might change in the future.
     * Refer to https://reactjs.org/docs/concurrent-mode-suspense.html from API changes
     * This "suspencyfies" the promise by throwing it, if it has not yet resolved and returning its result, if it did.
     *
     * @returns {T}             the resource
     * @throws  {Promise<void>} the suspender
     */
    _defineProperty(this, "read", () => {
      if (this.status === "unprepared" || typeof this.suspender === "undefined") {
        this.prepare();
        throw this.suspender;
      } else if (this.status === "pending") {
        throw this.suspender;
      } else if (this.status === "success") {
        return this.resource;
      } else {
        throw this.error;
      }
    });
    this.getter = getter;
  }
}
export class GlobalResource extends Resource {
  constructor(getter) {
    super(getter);
    _defineProperty(this, "ReactContext", /*#__PURE__*/React.createContext([{},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newResource => {
      // Noop since state updates during render are not supported
    }]));
    _defineProperty(this, "Provider", ({
      children
    }) => /*#__PURE__*/_jsx(SingleResourceProvider, {
      Resource: this,
      children: children
    }));
  }
}